import { type Conversation } from "shared/apiClient";
import { ConversationPhase, MessageType } from "shared/types";

import {
  useFindMessageOfType,
  useHasDisclaimerBeenAccepted,
  useMessageTypeExists,
} from "@/hooks";
import { useAtomValue } from "jotai";
import { debugMessageVar } from "@/variables/globalVar";

export const RenderType = {
  DEBUG_MESSAGE: "DEBUG_MESSAGE",
  QUESTIONS: "QUESTIONS",
  ROUTER: "ROUTER",
  REPORT: "REPORT",
  SYSTEM_ERROR: "SYSTEM_ERROR",
} as const;
export type RenderType = (typeof RenderType)[keyof typeof RenderType];

export function useRenderType(
  conversation: Conversation | undefined | null,
): RenderType {
  const debugMessage = useAtomValue(debugMessageVar);

  const systemErrorExists = useMessageTypeExists({
    messageType: MessageType.SYSTEM_ERROR,
  });

  const routingQuestionMessage = useFindMessageOfType({
    messageType: MessageType.ROUTING_QUESTION,
  });

  const disclaimerAcceptanceMessage = useFindMessageOfType({
    messageType: MessageType.DISCLAIMER_ACCEPTANCE,
  });

  const offTopic = conversation?.phase === ConversationPhase.off_topic;

  const conversationEnded = [
    ConversationPhase.conv_routing,
    ConversationPhase.ready_for_assessment,
    ConversationPhase.report_ready,
    ConversationPhase.amendment,
    ConversationPhase.saved_report,
    ConversationPhase.sent_to_council,
    ConversationPhase.off_topic,
  ].includes(conversation?.phase as any);

  // TODO: the follow hook call can be removed once we move over to
  //  conversation routing
  const disclaimerAccepted = useHasDisclaimerBeenAccepted(conversation);

  if (debugMessage) {
    return RenderType.DEBUG_MESSAGE;
  }

  if (systemErrorExists) {
    return RenderType.SYSTEM_ERROR;
  }

  if (disclaimerAcceptanceMessage) {
    return RenderType.REPORT;
  }
  if (routingQuestionMessage && !conversationEnded) {
    return RenderType.ROUTER;
  }

  // TODO: the following conditions can be removed once we move over to
  //  conversation routing
  if (offTopic) {
    return RenderType.ROUTER;
  }

  if (conversationEnded && !disclaimerAccepted) {
    return RenderType.ROUTER;
  }

  if (conversationEnded && disclaimerAccepted) {
    return RenderType.REPORT;
  }

  return RenderType.QUESTIONS;
}
